import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import { signOutPush } from '@helpers/push/signOutPush';
import ActionTypes, {
    fetchLoginUserProfileFailure,
    fetchLoginUserProfileSuccess,
    logoutUserProfileFailure,
    logoutUserProfileSuccess,
    updateUserProfileFailure,
    updateUserProfileSuccess
} from './actions';

interface LoginPayload {
  email: string;
  password: string;
  /** The bot check's fields (components/Common/BotCheck), spread into the body beside the credentials. */
  bot?: Record<string, string>;
}

interface UserApiResponseType {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.message ||
  fallback;

export function* fetchUserProfileSaga(action: {
  type: string;
  payload: LoginPayload;
}) {
  try {
    const { email, password, bot } = action.payload;
    const response: AxiosResponse<UserApiResponseType> = yield call(
      axios.post,
      '/api/auth/login',
      { ...bot, email, password },
    );
    yield put(fetchLoginUserProfileSuccess(response.data));
  } catch (error: any) {
    yield put(fetchLoginUserProfileFailure(getErrorMessage(error, 'Login failed. Please try again.')));
  }
}

// Logout the user
function* logoutUserProfileSaga() {
  try {
    // Release this browser's push token first: deleting it on the backend needs the session the
    // logout call destroys, and a token left behind sends this person's alerts to whoever signs in
    // here next. Bounded and never throws, so it cannot hold up a sign-out.
    yield call(signOutPush);
    yield call(axios.post, '/api/auth/logout');
    yield put(logoutUserProfileSuccess());
  } catch (error: any) {
    yield put(logoutUserProfileFailure(getErrorMessage(error, 'Logout failed. Please try again.')));
  }
}

// Watch for FETCH_USER_PROFILE_REQUEST action and call fetchUserProfileSaga
export function* loginUserProfileSaga() {
  yield takeLatest(
    ActionTypes.FETCH_LOGIN_USER_PROFILE_REQUEST,
    fetchUserProfileSaga,
  );
}

// Watch for LOGOUT_USER_PROFILE_REQUEST action and call logoutUserProfileSaga
export function* watchLogoutUserProfileSaga() {
  yield takeLatest(ActionTypes.LOGOUT_USER_PROFILE_REQUEST, logoutUserProfileSaga);
}

// Update user profile saga
function* updateUserProfileSaga(action: { type: string; payload: any }) {
  try {
    const response: AxiosResponse<any> = yield call(
      axios.put,
      '/api/user/update',
      action.payload,
    );
    yield put(updateUserProfileSuccess(response.data));
  } catch (error: any) {
    yield put(updateUserProfileFailure(getErrorMessage(error, 'Update failed')));
  }
}

export function* watchUpdateUserProfileSaga() {
  yield takeLatest(ActionTypes.UPDATE_USER_PROFILE_REQUEST, updateUserProfileSaga);
}

function* fetchLoginUserProfileFromSessionSaga() {
  try {
    const response: AxiosResponse<UserApiResponseType> = yield call(
      axios.get,
      '/api/me',
    );
    yield put(fetchLoginUserProfileSuccess(response.data));
  } catch {
    // No active session on initial load is expected. Treat it as logged out,
    // not as a login failure to avoid showing an error banner by default.
    yield put(logoutUserProfileSuccess());
  }
}

// Watch for FETCH_USER_PROFILE_FROM_SESSION_REQUEST action and call fetchUserProfileFromSessionSaga
export function* loginUserFromSessionSaga() {
  yield takeLatest(
    ActionTypes.FETCH_LOGIN_USER_PROFILE_FROM_SESSION_REQUEST,
    fetchLoginUserProfileFromSessionSaga,
  );
}
